import { DeleteObjectsCommand, PutObjectCommand } from '@aws-sdk/client-s3';
import { Service } from 'typedi';
import { aws, THUMBNAIL_HEIGHT, THUMBNAIL_WIDTH } from '../../../config';
import { isS3Enabled, s3, s3KeyFunction, s3MetadataFunction } from '../../../middlewares/storage';
import { HttpException } from '../../../utils/exceptions/httpException';
import { status } from '../../../utils/helpers/api.responses';
import Attachment from '../models/attachment.model';
import { IAttachment, IAttachmentResponse } from '../interfaces/attcahment.interface';
import fs from 'fs';
import path from 'path';
import { Request } from 'express';
import { TFunction } from 'i18next';
import { ClientSession, Types } from 'mongoose';
import { getUploadTypeFromMimeType } from '../../../utils/helpers/attachment.helper';
import { UPLOAD_TYPES } from '../../../utils/types/atttachment/attachment.type';
import sharp from 'sharp';
import ffmpeg from 'fluent-ffmpeg';
import ffmpegPath from '@ffmpeg-installer/ffmpeg';
import os from 'os';
import { randomUUID } from 'crypto';
ffmpeg.setFfmpegPath(ffmpegPath.path);

@Service()
export class AttachmentService {


    private async uploadSingleFile(
        file: Express.Multer.File & { moduleType?: string },
        options: {
            moduleName: string;
            moduleType?: string;
            uploadedBy: string;
            entityId: string;
            entityModel: string;
        },
        req: Request,
        t: (key: string, options?: any) => string
    ): Promise<IAttachmentResponse> {
        try {
            const { moduleName, moduleType, uploadedBy, entityId, entityModel } = options;
            const { mimetype, size: fileSize, originalname: filename, buffer } = file;
            if (!aws.S3_BUCKET_NAME || !s3) {
                throw new Error('S3 configuration is incomplete');
            }
            let key: string;
            const keyPromise = await new Promise<string>((resolve, reject) => {
                s3KeyFunction(
                    { query: { moduleName, entityId }, originalUrl: '/Gard-Labour-Hire' },
                    { ...file, originalname: filename, mimetype, size: fileSize },
                    (err, result) => (err ? reject(err) : resolve(result!))
                );
            });
            let metadata: any;
            const metadataPromise = new Promise<any>((resolve, reject) => {
                s3MetadataFunction(
                    { ...file, originalname: filename, mimetype, size: fileSize },
                    (err, result) => {
                        if (err) {
                            console.error('Error generating S3 metadata:', err);
                            reject(err);
                        } else {
                            console.log('S3 metadata generated:', result);
                            resolve(result);
                        }
                    }
                );
            });

            [key, metadata] = await Promise.all([keyPromise, metadataPromise])

            const url = `https://${aws.S3_BUCKET_NAME}.s3.amazonaws.com/${key}`;

            let thumbnailKey: string | undefined;
            let thumbnailUrl: string | undefined;


            const type = getUploadTypeFromMimeType(mimetype);
            console.log("type", type)
            if (type.toLowerCase() === UPLOAD_TYPES.IMAGE.toLowerCase()) {
                const thumbnailBuffer = await sharp(buffer)
                    .resize(Number(THUMBNAIL_WIDTH), Number(THUMBNAIL_HEIGHT), {
                        fit: 'cover',
                        position: 'center'
                    })
                    .toBuffer();
                const fileExtension = path.extname(key);
                const baseKey = key.substring(0, key.length - fileExtension.length);
                thumbnailKey = `${baseKey}_thumb.jpg`;
                thumbnailUrl = `https://${aws.S3_BUCKET_NAME}.s3.amazonaws.com/${thumbnailKey}`;
                if (isS3Enabled) {
                    const thumbnailUploadParams = {
                        Bucket: aws.S3_BUCKET_NAME,
                        Key: thumbnailKey,
                        Body: thumbnailBuffer,
                        ContentType: 'image/jpeg',
                        Metadata: metadata,
                    };
                    await s3.send(new PutObjectCommand(thumbnailUploadParams));
                }
            } else if (type.toLowerCase() === UPLOAD_TYPES.VIDEO.toLowerCase()) {
                const thumbnailBuffer = await this.generateVideoThumbnail(buffer);

                const fileExtension = '.jpg';
                const baseKey = key.substring(0, key.length - path.extname(key).length);
                thumbnailKey = `${baseKey}_thumb${fileExtension}`;
                thumbnailUrl = `https://${aws.S3_BUCKET_NAME}.s3.amazonaws.com/${thumbnailKey}`;

                if (isS3Enabled) {

                    const thumbnailUploadParams = {
                        Bucket: aws.S3_BUCKET_NAME,
                        Key: thumbnailKey,
                        Body: thumbnailBuffer,
                        ContentType: 'image/jpeg',
                        Metadata: metadata,
                    };
                    await s3.send(new PutObjectCommand(thumbnailUploadParams));
                }
            } else if (type.toLowerCase() === UPLOAD_TYPES.DOCUMENT.toLowerCase()) {
                const { PDFDocument } = await import('pdf-lib');
                const { default: sharp } = await import('sharp');

                const pdfDoc = await PDFDocument.load(buffer);
                const page = pdfDoc.getPages()[0];
                const { width, height } = page.getSize();

                const pngBuffer = await sharp({
                    create: {
                        width: Math.floor(width),
                        height: Math.floor(height),
                        channels: 4,
                        background: { r: 255, g: 255, b: 255, alpha: 1 }
                    }
                }).png().toBuffer();

                const thumbnailBuffer = await sharp(pngBuffer)
                    .resize(Number(THUMBNAIL_WIDTH), Number(THUMBNAIL_HEIGHT), {
                        fit: 'cover',
                        position: 'center'
                    })
                    .toBuffer();

                const fileExtension = '.jpg';
                const baseKey = key.substring(0, key.length - path.extname(key).length);
                thumbnailKey = `${baseKey}_thumb${fileExtension}`;
                thumbnailUrl = `https://${aws.S3_BUCKET_NAME}.s3.amazonaws.com/${thumbnailKey}`;

                if (isS3Enabled) {
                    const thumbnailUploadParams = {
                        Bucket: aws.S3_BUCKET_NAME,
                        Key: thumbnailKey,
                        Body: thumbnailBuffer,
                        ContentType: 'image/jpeg',
                        Metadata: metadata,
                    };
                    await s3.send(new PutObjectCommand(thumbnailUploadParams));
                }
            }


            await s3.send(new PutObjectCommand({
                Bucket: aws.S3_BUCKET_NAME,
                Key: key,
                Body: buffer,
                ContentType: mimetype,
                Metadata: metadata
            }));

            const doc = await Attachment.create({
                key,
                mimetype,
                filename,
                fileSize,
                type: moduleName,
                moduleType: moduleType || moduleName,
                uploadedBy,
                isTemporary: true,
                entityId,
                entityModel,
                metadata: {},
                isDeleted: false,
                deletedAt: null,
                thumbnailKey
            });

            return {
                _id: doc._id,
                key: doc.key,
                filename: doc.filename,
                fileSize: doc.fileSize,
                moduleType: doc.moduleType,
                thumbnailUrl,
                url,
            } as IAttachmentResponse;

        } catch (err: any) {
            throw new HttpException(
                status.BadRequest,
                t('Upload.upload_failed', { field: file.originalname }) || err.message
            );
        }
    }


    public async handleUpload(
        files: (Express.Multer.File & { moduleType?: string })[],
        options: {
            moduleName: string;
            moduleType?: string;
            uploadedBy: string;
            entityId: string;
            entityModel: string;
        },
        req: Request,
        t: (key: string, options?: any) => string
    ): Promise<{ uploadedFiles: IAttachmentResponse[]; failedFiles: { filename: string; reason: string }[] }> {
        const uploadedFiles: IAttachmentResponse[] = [];
        const failedFiles: { filename: string; reason: string }[] = [];

        await Promise.all(
            files.map(async (file) => {
                try {
                    const uploaded = await this.uploadSingleFile(file, options, req, t);
                    uploadedFiles.push(uploaded);
                } catch (err: any) {
                    failedFiles.push({
                        filename: file.originalname,
                        reason: err.message ?? t('Upload.upload_failed'),
                    });
                }
            })
        );

        if (uploadedFiles.length === 0) {
            throw new HttpException(
                status.BadRequest,
                t('Upload.no_file_uploaded')
            );
        }

        return {
            uploadedFiles,
            failedFiles,
        };
    }

    private async generateVideoThumbnail(buffer: Buffer): Promise<Buffer> {
        const tmpDir = os.tmpdir();
        const inputPath = path.join(tmpDir, `${randomUUID()}.mp4`);
        const outputPath = path.join(tmpDir, `${randomUUID()}.jpg`);

        try {
            await fs.promises.writeFile(inputPath, buffer);

            return new Promise((resolve, reject) => {
                ffmpeg(inputPath)
                    .output(outputPath)
                    .outputOptions([
                        '-vframes 1',
                        '-ss 00:00:01',
                        `-vf scale=${Number(THUMBNAIL_WIDTH)}:${Number(THUMBNAIL_HEIGHT)}:force_original_aspect_ratio=decrease,pad=${Number(THUMBNAIL_WIDTH)}:${Number(THUMBNAIL_HEIGHT)}:(ow-iw)/2:(oh-ih)/2`
                    ])
                    .on('end', async () => {
                        try {
                            const thumbnailBuffer = await fs.promises.readFile(outputPath);
                            await fs.promises.unlink(inputPath);
                            await fs.promises.unlink(outputPath);
                            resolve(thumbnailBuffer);
                        } catch (err) {
                            reject(err);
                        }
                    })
                    .on('error', async (err) => {
                        await fs.promises.unlink(inputPath).catch(() => { });
                        reject(new Error(`Failed to generate video thumbnail: ${err.message}`));
                    })
                    .run();
            });
        } catch (error) {
            if (error instanceof HttpException) throw error
            throw new Error('Thumbnail generation failed');
        }
    }



    public async getImages(t: (key: string, options?: any) => string, moduleName?: string) {
        try {
            const docs = moduleName
                ? await Attachment.find({ type: moduleName, isDeleted: false }).lean()
                : await Attachment.find({ isDeleted: false }).lean();

            return docs.map((doc) => ({
                url: isS3Enabled
                    ? `${aws.S3_BASE_URL}/${doc.key}`
                    : `http://localhost:${process.env.PORT || 3000}/uploads/${doc.key}`,
                mimetype: doc.mimetype,
                attachmentId: doc._id,
                isTemporary: doc.isTemporary,
                type: doc.type,
                moduleType: doc.moduleType,
                filename: doc.filename,
                thumbnailUrl: `${aws.S3_BASE_URL}/${doc.thumbnailKey}`
            }));
        } catch (error) {
            if (error instanceof HttpException) throw error;

            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Upload.file') })
            );
        }
    }

    public async deleteDocuments(ids: string[], t: (key: string, options?: any) => string): Promise<string[]> {
        try {
            const docs = await Attachment.find({ _id: { $in: ids } }).lean();

            if (!docs.length) {
                throw new HttpException(
                    status.NotFound,
                    t('General.not_found', { field: t('Upload.file') })
                );
            }

            const keys = docs.map((doc) => doc.key);

            if (keys.length > 0 && isS3Enabled) {
                const deleteParams = {
                    Bucket: aws.S3_BUCKET_NAME,
                    Delete: { Objects: keys.map((key) => ({ Key: key })) },
                };
                await s3.send(new DeleteObjectsCommand(deleteParams));
            } else if (keys.length > 0) {
                keys.forEach((key) => {
                    const filePath = path.join(process.env.STORAGE_PATH || 'uploads', key);
                    if (fs.existsSync(filePath)) {
                        fs.unlinkSync(filePath);
                    }
                });
            }

            await Attachment.deleteMany({ _id: { $in: ids } });

            // await Attachment.updateMany(
            //     { _id: { $in: ids } },
            //     { $set: { isDeleted: true, deletedAt: new Date(), isTemporary: true } }
            // );



            return docs.map((doc) => doc.filename);
        } catch (error) {
            if (error instanceof HttpException) throw error;

            throw new HttpException(
                status.InternalServerError,
                t('General.errorDeleting', { field: t('Upload.file') })
            );
        }
    }

    public async deleteTemporaryFiles(t: (key: string, options?: any) => string): Promise<void> {
        try {
            const tempDocs = await Attachment.find({ isTemporary: true, isDeleted: true }).lean();

            if (tempDocs.length > 0) {
                const keys = tempDocs.map((doc) => doc.key);

                if (isS3Enabled) {
                    const deleteParams = {
                        Bucket: aws.S3_BUCKET_NAME,
                        Delete: { Objects: keys.map((key) => ({ Key: key })) },
                    };
                    await s3.send(new DeleteObjectsCommand(deleteParams));
                } else {
                    keys.forEach((key) => {
                        const filePath = path.join(process.env.STORAGE_PATH || 'uploads', key);
                        if (fs.existsSync(filePath)) {
                            fs.unlinkSync(filePath);
                        }
                    });
                }

                await Attachment.deleteMany({ _id: { $in: tempDocs.map((doc) => doc._id) } });
            }
        } catch (error) {
            if (error instanceof HttpException) throw error;

            throw new HttpException(
                status.InternalServerError,
                t('General.errorDeleting', { field: t('Upload.tempFile') })
            );
        }
    }


    public async updateIsTemporaryFalse(documentIds: string | string[], t: TFunction, session?: ClientSession): Promise<void> {
        try {
            const ids = Array.isArray(documentIds) ? documentIds : [documentIds];
            await Attachment.updateMany(
                { _id: { $in: ids } },
                { $set: { isTemporary: false, isDeleted: false, deletedAt: null } },
                { session: session || undefined }
            );
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(status.InternalServerError, t('General.errorUpdating', { field: t('Upload.image') }));
        }
    }
    // updateMatliIsTemporaryFalse


    public async updateMatliIsTemporaryFalse(
        documentIds: string | string[],
        t: TFunction,
        session?: ClientSession
    ): Promise<void> {
        try {

            const ids = Array.isArray(documentIds)
                ? documentIds.flatMap(id => id.split(','))
                : String(documentIds).split(',');
            if (!ids.every(id => Types.ObjectId.isValid(id))) {
                throw new HttpException(
                    status.BadRequest,
                    t('General.invalid', { field: t('Upload.file') })
                );
            }
            await Attachment.updateMany(
                { _id: { $in: ids }, isDeleted: false },
                { $set: { isTemporary: false, isDeleted: false, deletedAt: null } },
                { session: session || undefined }
            );
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.InternalServerError,
                t('General.errorUpdating', { field: t('Upload.image') })
            );
        }
    }
    public async getAttachmentsByIds(
        ids: (string | Types.ObjectId)[],
        t: TFunction,
        session?: ClientSession
    ): Promise<IAttachment[]> {
        try {
            const idArray = Array.isArray(ids) ? ids : [ids];
            const objectIds = idArray.map(id => new Types.ObjectId(id));
            return await Attachment.find(
                { _id: { $in: objectIds }, isDeleted: false },
            ).session(session || null);;
        } catch (error) {
            if (error instanceof HttpException) throw error;
            throw new HttpException(
                status.BadRequest,
                t('General.invalid', { field: t('Upload.file') })
            );
        }
    }


    public async getById(id: string, t: (key: string, options?: any) => string): Promise<IAttachmentResponse> {
        try {
            const doc = await Attachment.findOne({ _id: id, isDeleted: false }).lean();

            if (!doc) {
                throw new HttpException(
                    status.NotFound,
                    t('General.not_found', { field: t('Upload.file') })
                );
            }

            return {
                _id: doc._id,
                type: doc.type,
                mimetype: doc.mimetype,
                key: doc.key,
                filename: doc.filename,
                fileSize: doc.fileSize,
                moduleType: doc.moduleType,
                url: isS3Enabled
                    ? `${aws.S3_BASE_URL}/${doc.key}`
                    : `http://localhost:${process.env.PORT || 3000}/Uploads/${doc.key}`,
                thumbnailUrl: `${aws.S3_BASE_URL}/${doc.thumbnailKey}`
            };
        } catch (error) {
            if (error instanceof HttpException) throw error;

            throw new HttpException(
                status.InternalServerError,
                t('General.errorFetching', { field: t('Upload.file') })
            );
        }
    }



}